import useApproveOrRejectReturn from "@babackapp/private-api-client/client/use-approve-or-reject-return";
import { useUpdateReturnRequestInCache } from "@babackapp/private-api-client/client/use-invalidate-return-request";
import useTranslation from "@babackapp/translations/use-translation";
import { MessageCircleWarning } from "lucide-react";
import { useState } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useViewerHasAtLeastCustomerSupportRights } from "@/lib/rights/use-viewer-has-right";
import { useCurrentReturnRequest } from "@/pages/(app)/returns/utils/current-return-request-context";
import {
  DisapproveReturnDialog,
  type DisapprovalPayload,
} from "@/pages/(app)/returns/view/components/disapprove-return/disapprove-return-dialog";
import { isDisapprovalReasonAsked } from "@/pages/(app)/returns/view/components/disapprove-return/is-disapproval-reason-asked";

export function useRejectReturnRequestAction() {
  const { data: shop } = useCurrentShopSuspense();
  const returnRequest = useCurrentReturnRequest();
  const t = useTranslation();
  const hasCustomerRight = useViewerHasAtLeastCustomerSupportRights();
  // `.done` on purpose: a refusal counts as decided too (BAB-3194).
  const alreadyDecided = returnRequest.status.approved.done;
  const isCancelled = returnRequest.status.cancelled.done;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const updateReturnInReturnRequestInfiniteCache =
    useUpdateReturnRequestInCache();
  const { mutate, isPending } = useApproveOrRejectReturn({
    onSuccess(returnRequest) {
      updateReturnInReturnRequestInfiniteCache({ returnRequest });
      setIsDialogOpen(false);
    },
  });

  const asksForReason = isDisapprovalReasonAsked(shop);

  const disapprove = (payload?: DisapprovalPayload) =>
    mutate({
      shopId: shop.id,
      returnRequestId: returnRequest.id,
      status: "rejected",
      ...payload,
    });

  return {
    label: t("returnDetail.rejectedRequest"),
    enabled: hasCustomerRight && !alreadyDecided && !isCancelled,
    icon: <MessageCircleWarning className="h-5 w-5 text-destructive" />,
    loading: isPending,
    // The shop toggle is the only switch: off, the one-click disapproval of the
    // ~990 shops that never enable the capture is untouched.
    onClick: () => (asksForReason ? setIsDialogOpen(true) : disapprove()),
    // Mounted only once open: the dialog reads the shop's workflows through a
    // suspense query, which would otherwise blank the whole return page on
    // every load — including returns nobody can disapprove.
    dialog: isDialogOpen ? (
      <DisapproveReturnDialog
        returnRequest={returnRequest}
        open
        onOpenChange={setIsDialogOpen}
        onConfirm={disapprove}
        isPending={isPending}
      />
    ) : null,
  };
}
